/**
 * TaskBoard — a Kanban board view of tasks, one column per status
 * (To Do / In Progress / Done). Built on @dnd-kit (the codebase's DnD lib, also
 * the 2026 community standard: accessible keyboard + screen-reader support,
 * WCAG 2.1 AA out of the box) using the canonical multi-column sortable pattern:
 * a DndContext wraps a per-column SortableContext of useSortable cards, with a
 * DragOverlay for the dragged card and closestCorners collision detection.
 *
 * Persistence: dragging a card to ANOTHER column changes its status via the
 * onStatusChange PROP (the same optimistic mutation the table uses — this leaf
 * is react-query-free). Within-column reordering is ephemeral (tasks carry no
 * position column), so it is a local nicety that resets on the next refetch.
 */

import { useState, useEffect, useRef } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { DotsThreeVertical, PencilSimple, Trash } from "@phosphor-icons/react";
import { TagPill } from "../clients/TagPill";
import { ClientAvatar } from "../clients/ClientAvatar";
import type { Task, TaskStatus } from "./types";
import {
  STATUS_OPTIONS,
  STATUS_STYLES,
  STATUS_ICONS,
  statusLabel,
  assigneeLabel,
  dueLabel,
  isOverdue,
} from "./taskDisplay";


const STATUSES: readonly TaskStatus[] = ["todo", "in_progress", "done"];
type Columns = Record<TaskStatus, Task[]>;

function groupByStatus(tasks: Task[]): Columns {
  return {
    todo: tasks.filter((t) => t.status === "todo"),
    in_progress: tasks.filter((t) => t.status === "in_progress"),
    done: tasks.filter((t) => t.status === "done"),
  };
}

/** Which column an id belongs to — a column id (empty-column drop) or a card id. */
function findColumnOf(id: string, cols: Columns): TaskStatus | null {
  if ((STATUSES as readonly string[]).includes(id)) return id as TaskStatus;
  for (const s of STATUSES) {
    if (cols[s].some((t) => t.id === id)) return s;
  }
  return null;
}

interface TaskBoardProps {
  tasks: Task[];
  canManage: boolean;
  onTaskClick?: (task: Task) => void;
  onEditClick?: (task: Task) => void;
  onDeleteClick?: (task: Task) => void;
  onStatusChange?: (task: Task, status: TaskStatus) => void;
}

/** The card's visual content — shared by the in-column card and the drag overlay. */
function CardBody({
  task,
  canManage,
  onEditClick,
  onDeleteClick,
}: {
  task: Task;
  canManage: boolean;
  onEditClick?: (task: Task) => void;
  onDeleteClick?: (task: Task) => void;
}) {
  const overdue = isOverdue(task.dueDate, task.status);
  return (
    <div
      className="rounded-[16px] bg-background shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <div className="flex items-start gap-2 px-3 pt-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-[13.5px] font-medium leading-snug">
            {task.title}
          </h3>
          {task.description && (
            <p className="mt-0.5 text-[13px] text-foreground line-clamp-2">
              {task.description}
            </p>
          )}
        </div>
        {canManage && (
          <div
            className="-mr-1 -mt-1 shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="touch-target-sm min-h-7 min-w-7 cursor-pointer rounded-lg border border-transparent hover:bg-muted hover:border-input transition-all"
                  aria-label={`Actions for ${task.title}`}
                >
                  <DotsThreeVertical size={18} weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-xl p-2 shadow-md"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                <DropdownMenuItem
                  className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                  onClick={() => onEditClick?.(task)}
                >
                  <PencilSimple size={14} className="mr-1" />
                  Edit Task
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                  onClick={() => onDeleteClick?.(task)}
                >
                  <Trash size={14} className="mr-1" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="px-3 pt-3 pb-3 space-y-2">
        {task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 pb-1">
            {task.tags.map((tag) => (
              <TagPill key={tag.id} tag={tag} />
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 text-[13.5px] text-foreground pb-1">
          {task.clientName ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <ClientAvatar
                name={task.clientName}
                logoUrl={task.clientLogoUrl}
                size="sm"
                className="!h-5 !w-5 !text-[9px]"
              />
              <span className="truncate">{task.clientName}</span>
            </span>
          ) : (
            <span>General</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[13.5px] text-foreground">
          <span className="truncate min-w-0">
            {assigneeLabel(task.assigneeName)}
          </span>
          <span
            className={`ml-auto shrink-0 whitespace-nowrap ${overdue ? "text-destructive font-medium" : ""}`}
          >
            {dueLabel(task.dueDate)}
          </span>
        </div>
      </div>
    </div>
  );
}

/** A sortable (draggable) card inside a column. */
function SortableCard({
  task,
  canManage,
  onTaskClick,
  onEditClick,
  onDeleteClick,
}: {
  task: Task;
  canManage: boolean;
  onTaskClick?: (task: Task) => void;
  onEditClick?: (task: Task) => void;
  onDeleteClick?: (task: Task) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canManage });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      data-testid={`board-card-${task.id}`}
      className={`${canManage ? "cursor-grab active:cursor-grabbing" : ""} ${isDragging ? "opacity-40" : ""}`}
      // NO onKeyDown here: it would clobber dnd-kit's keyboard drag activator
      // (listeners.onKeyDown). Space/Enter on a focused card picks it up for a
      // keyboard drag; editing is via click or the keyboard-reachable 3-dot menu.
      onClick={() => onTaskClick?.(task)}
    >
      <CardBody
        task={task}
        canManage={canManage}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
      />
    </div>
  );
}

/** A status column: a header pill + a droppable, sortable list of cards. */
function BoardColumn({
  status,
  tasks,
  canManage,
  onTaskClick,
  onEditClick,
  onDeleteClick,
}: {
  status: TaskStatus;
  tasks: Task[];
  canManage: boolean;
  onTaskClick?: (task: Task) => void;
  onEditClick?: (task: Task) => void;
  onDeleteClick?: (task: Task) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const StatusIcon = STATUS_ICONS[status];
  return (
    <div
      className="flex flex-col rounded-[22px] shadow-xs"
      style={{
        backgroundColor: "#fbfcfe",
        border: "0.5px solid var(--hairline)",
      }}
    >
      <div className="flex items-center gap-2 px-4 py-3">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
        >
          <StatusIcon size={12} weight="bold" />
          {statusLabel(status)}
        </span>
        <span
          className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
        >
          {tasks.length}
        </span>
      </div>
      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          data-testid={`board-column-${status}`}
          className={`flex-1 min-h-[120px] space-y-2 px-3 pb-3 rounded-b-[22px] transition-colors ${isOver ? "bg-brand/[0.06]" : ""}`}
        >
          {tasks.map((task) => (
            <SortableCard
              key={task.id}
              task={task}
              canManage={canManage}
              onTaskClick={onTaskClick}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
          {tasks.length === 0 && (
            <div className="flex items-center justify-center h-20 text-[12px] text-muted-foreground/70">
              Drop tasks here
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

export function TaskBoard({
  tasks,
  canManage,
  onTaskClick,
  onEditClick,
  onDeleteClick,
  onStatusChange,
}: TaskBoardProps) {
  const [columns, setColumns] = useState<Columns>(() => groupByStatus(tasks));
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const draggingRef = useRef(false);

  // Re-sync local columns from the tasks prop whenever it changes and we are NOT
  // mid-drag (refetch after a status change, filter/search updates, etc.).
  useEffect(() => {
    if (!draggingRef.current) setColumns(groupByStatus(tasks));
  }, [tasks]);

  const sensors = useSensors(
    // 8px before a drag activates, so a click (edit / 3-dot menu) is not a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragStart(event: DragStartEvent) {
    draggingRef.current = true;
    const id = String(event.active.id);
    const found = findColumnOf(id, columns);
    setActiveTask(found ? columns[found].find((t) => t.id === id) ?? null : null);
  }

  // Move the dragged card into the column it is hovering, in local state, so the
  // gap/placeholder follows the cursor across columns.
  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const from = findColumnOf(activeId, columns);
    const to = findColumnOf(overId, columns);
    if (!from || !to || from === to) return;

    setColumns((prev) => {
      const fromItems = [...prev[from]];
      const toItems = [...prev[to]];
      const activeIdx = fromItems.findIndex((t) => t.id === activeId);
      if (activeIdx === -1) return prev;
      const [moved] = fromItems.splice(activeIdx, 1);
      if (!moved) return prev;
      const overIdx = toItems.findIndex((t) => t.id === overId);
      const insertIdx = overIdx >= 0 ? overIdx : toItems.length;
      toItems.splice(insertIdx, 0, { ...moved, status: to });
      return { ...prev, [from]: fromItems, [to]: toItems };
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    draggingRef.current = false;
    const moved = activeTask;
    setActiveTask(null);
    const { active, over } = event;
    if (!over || !moved) {
      setColumns(groupByStatus(tasks));
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    const to = findColumnOf(overId, columns);
    if (!to) {
      // Unresolvable drop target — reconcile local state back to the source of
      // truth (symmetric with the `!over` branch above) so nothing stays stuck.
      setColumns(groupByStatus(tasks));
      return;
    }

    // Within-column reorder (ephemeral — no position column to persist to).
    setColumns((prev) => {
      const items = [...prev[to]];
      const oldIndex = items.findIndex((t) => t.id === activeId);
      const newIndex = items.findIndex((t) => t.id === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        return { ...prev, [to]: arrayMove(items, oldIndex, newIndex) };
      }
      return prev;
    });

    // Persist the cross-column move as a status change (optimistic, owned by the page).
    if (moved.status !== to && onStatusChange) {
      onStatusChange(moved, to);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        draggingRef.current = false;
        setActiveTask(null);
        setColumns(groupByStatus(tasks));
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        {STATUS_OPTIONS.map((opt) => (
          <BoardColumn
            key={opt.value}
            status={opt.value}
            tasks={columns[opt.value]}
            canManage={canManage}
            onTaskClick={onTaskClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
          />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? (
          <div className="rotate-2 cursor-grabbing">
            <CardBody task={activeTask} canManage={false} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
